import { inject, Injectable, signal } from '@angular/core';
import { map, of, take } from 'rxjs';
import { hasMaasBilling, MenuPointToken, Response, Vehicle } from '@agilox/common';
import { desktopMenuPoints } from '../constants/desktop-menupoints.const';
import { mobileMenupoints } from '../constants/mobile-menupoints.const';
import { MenuPointUrl } from '../enums/menupoint-url.enum';
import { MaasService } from '../../pages/maas/maas.service';
import { MenuPoint } from '@agilox/ui-common';
import { UserStoreService } from '@user-store';

@Injectable({
	providedIn: 'root',
})
export class MenuProcessingService {
	private userStoreService = inject(UserStoreService);
	private maasService = inject(MaasService);

	private _processedMenuPoints = signal<MenuPointToken>({
		desktop: desktopMenuPoints,
		mobile: mobileMenupoints,
	});

	private _initialized = signal(false);

	public processedMenuPoints = this._processedMenuPoints.asReadonly();

	initializeMenuPoints() {
		if (this._initialized()) {
			return of(this._processedMenuPoints());
		}

		const user = this.userStoreService.currentUser();
		if (!user) {
			return of(this._processedMenuPoints());
		}

		return this.maasService.fetchVehicles('', { number: 0, size: 1 }).pipe(
			take(1),
			map((data: Response<Vehicle>) => {
				const vehicleCount = data.meta?.total ?? 0;
				const maasBilling = hasMaasBilling(user);

				const menuPoints = {
					desktop: this.processMaasMenuPoint(desktopMenuPoints, maasBilling, vehicleCount),
					mobile: this.processMaasMenuPoint(mobileMenupoints, maasBilling, vehicleCount),
				};

				this._processedMenuPoints.set(menuPoints);
				this._initialized.set(true);
				return menuPoints;
			})
		);
	}

	private processMaasMenuPoint(
		menuPoints: MenuPoint[],
		hasMaasBilling: boolean,
		vehicleCount: number
	): MenuPoint[] {
		const clonedMenu = structuredClone(menuPoints);
		const MAAS_SUBMENUS = [
			{ name: 'maas.submenu.analysis', url: '/maas/analysis', roles: [] },
			{ name: 'maas.submenu.overview', url: '/maas/overview', roles: [] },
			{ name: 'maas.submenu.annualOverview', url: '/maas/annual-overview', roles: [] },
		];

		const updateMaasItem = (item: any) => {
			if (!hasMaasBilling && vehicleCount > 0) {
				return null; // Mark for removal
			}
			if (vehicleCount === 0) {
				return { ...item, url: '/maas/types', submenus: undefined };
			}
			return { ...item, url: MenuPointUrl.MAAS, submenus: MAAS_SUBMENUS };
		};

		// Handle top-level MaaS menu
		const maasIndex = clonedMenu.findIndex((p: any) => p.url === MenuPointUrl.MAAS);
		if (maasIndex !== -1) {
			const updated = updateMaasItem(clonedMenu[maasIndex]);
			return updated
				? [...clonedMenu.slice(0, maasIndex), updated, ...clonedMenu.slice(maasIndex + 1)]
				: clonedMenu.filter((_, i) => i !== maasIndex);
		}

		// Handle MaaS in submenus
		clonedMenu.forEach((menu: any) => {
			if (!menu.submenus?.length) return;
			const subIndex = menu.submenus.findIndex((s: any) => s.url === MenuPointUrl.MAAS);
			if (subIndex !== -1) {
				const updated = updateMaasItem(menu.submenus[subIndex]);
				if (updated === null) {
					menu.submenus.splice(subIndex, 1);
				} else {
					menu.submenus[subIndex] = updated;
				}
			}
		});

		return clonedMenu;
	}
}
